import { useState } from 'react';
import { Box, Card, Chip, Stack, Typography, useTheme, type Theme } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { AdminAuditLogFilters, AuditLogEntry } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { CustomDataGrid, type DataGridColumn } from '@/components/shell/CustomDataGrid';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import { whitelistedMetadataEntries } from './domain';
import { AuditEntryDetailDialog } from './AuditEntryDetailDialog';

interface AuditLogListViewProps {
  filters: AdminAuditLogFilters;
  onFiltersChange: (patch: Partial<AdminAuditLogFilters>) => void;
}

/**
 * `GET /v1/admin/audit-log` — readable by `platform:admin` and
 * `platform:auditor` alike (the auditor is a first-class reader here, not a
 * degraded one). Every call is itself recorded as `audit_log.queried` by the
 * API; nothing extra is required from this screen for that.
 *
 * The contract has no per-row detail endpoint and no `sort` parameter for
 * this list (unlike `/v1/admin/reports/operations`) — rows arrive already ordered
 * by the API, and a row's full record (including `metadata`) is already
 * present on the list item, so the detail dialog opens from data already in
 * hand rather than issuing a second request.
 */
export function AuditLogListView({ filters, onFiltersChange }: AuditLogListViewProps) {
  const translate = useTranslate();
  const theme = useTheme();
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | undefined>(undefined);
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.listAdminAuditLog(filters),
    JSON.stringify(filters),
  );

  if (loading) {
    return (
      <Card>
        <LoadingState variant="list" rows={8} />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'access_denied') {
      return (
        <Card>
          <AccessDenied />
        </Card>
      );
    }
    if (error.errorCode === 'tenant_not_authorized') {
      return (
        <Card>
          <AccessDenied body={error.message} />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.audit.list.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card>
        <EmptyState title={translate('pay.audit.list.emptyTitle')} body={translate('pay.audit.list.emptyBody')} />
      </Card>
    );
  }

  const monoSpanSx = (theme: Theme) => ({ fontFamily: theme.custom.fontFamilyMono, fontSize: '0.75rem' });

  /**
   * Raw identifiers (actor id, object id — UUIDs since 1.26.0.0) are the
   * cells that made rows very wide: an unbreakable 36-char mono string per
   * column. Ellipsize them at a fixed character budget and keep the full
   * value reachable on hover via the native `title` tooltip; the detail
   * dialog still shows everything. `EllipsizedId` never removes data.
   */
  const ellipsizedIdSx = (theme: Theme) => ({
    ...monoSpanSx(theme),
    display: 'inline-block',
    maxWidth: '24ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'bottom',
  });

  const renderEllipsizedId = (value: string) => (
    <Box component="span" title={value} sx={ellipsizedIdSx}>
      {value}
    </Box>
  );

  const columns: Array<DataGridColumn<AuditLogEntry>> = [
    {
      key: 'at',
      label: translate('pay.audit.list.columnAt'),
      nowrap: true,
      render: (item) => new Date(item.at).toLocaleString('es-ES'),
    },
    {
      key: 'action',
      label: translate('pay.audit.list.columnAction'),
      mono: 'code',
      render: (item) => translate(`pay.audit.action.${item.action}`, { _: item.action }),
    },
    {
      key: 'actor',
      label: translate('pay.audit.list.columnActor'),
      // Display name above the raw id (same layout the tenant column uses)
      // when the row captured one at write time; the id alone otherwise.
      render: (item) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={translate(`pay.audit.actorType.${item.actorType}`, { _: item.actorType })} />
          {item.actorDisplay ? (
            <Stack spacing={0} sx={{ minWidth: 0 }}>
              <Typography component="span" variant="body2">
                {item.actorDisplay}
              </Typography>
              {renderEllipsizedId(item.actor)}
            </Stack>
          ) : (
            renderEllipsizedId(item.actor)
          )}
        </Stack>
      ),
    },
    {
      key: 'object',
      label: translate('pay.audit.list.columnObject'),
      render: (item) => (
        <>
          <Chip size="small" sx={{ mr: 1 }} label={translate(`pay.audit.objectType.${item.objectType}`, { _: item.objectType })} />
          {renderEllipsizedId(item.objectId)}
        </>
      ),
    },
    {
      key: 'ip',
      label: translate('pay.audit.list.columnIp'),
      nowrap: true,
      render: (item) =>
        item.ip ? (
          <Box component="span" sx={monoSpanSx}>
            {item.ip}
          </Box>
        ) : (
          '—'
        ),
    },
    {
      key: 'metadata',
      label: translate('pay.audit.list.columnMetadata'),
      nowrap: true,
      // Presence indicator only — counted over the same whitelist the detail
      // dialog renders, so the number always matches what opening the row shows.
      render: (item) => {
        const count = whitelistedMetadataEntries(item.action, item.metadata).length;
        if (count === 0) return '—';
        return <Chip size="small" label={String(count)} />;
      },
    },
    {
      key: 'tenant',
      label: translate('pay.audit.list.columnTenant'),
      render: (item) => {
        if (!item.tenantCode) return translate('pay.audit.list.tenantPlatformWide');
        if (!item.tenantName) return item.tenantCode;
        return (
          <Stack spacing={0}>
            <Typography component="span" variant="body2">
              {item.tenantName}
            </Typography>
            <Box component="span" sx={monoSpanSx}>
              {item.tenantCode}
            </Box>
          </Stack>
        );
      },
    },
  ];

  return (
    <>
      <CustomDataGrid
        columns={columns}
        rows={data.items}
        // Below the `md` breakpoint's width the audit table scrolls inside
        // its card rather than crushing seven columns into the viewport.
        tableMinWidth={theme.breakpoints.values.md}
        getRowKey={(item) => item.id}
        onRowClick={(item) => setSelectedEntry(item)}
        rowAriaLabel={translate('pay.audit.list.viewDetail')}
        pagination={{
          page: data.page.page,
          pageSize: data.page.pageSize,
          totalItems: data.page.totalItems,
          rowsPerPageLabel: translate('pay.audit.list.rowsPerPage'),
          onPageChange: (page) => onFiltersChange({ page }),
          onPageSizeChange: (pageSize) => onFiltersChange({ pageSize, page: 1 }),
        }}
      />
      <AuditEntryDetailDialog entry={selectedEntry} onClose={() => setSelectedEntry(undefined)} />
    </>
  );
}
